import {
  Box,
  HStack,
  Icon,
  IconButton,
  Image,
  Text,
  VStack,
} from '@chakra-ui/react';
import { CommentBadge } from '@web-app/components/Badge/CommentBadge';
import { parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useAtomValue } from 'jotai';
import { ReactNode, useEffect, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';

import { EntryComment } from '../EntryComment/EntryComment';
import { allEntriesExpandedAtom } from '../timelineAtoms';
import { EntryComment as EntryCommentType } from '../types';
import { resolveTimezone } from '../utils';

export interface JournalEntryCardProps {
  /** Unique identifier for the journal entry. */
  entryId: string;
  /** Icon element to visually represent the entry type/category. */
  icon: ReactNode;
  /** Title or label of the entry (often meal/event type), as a React element. */
  title: ReactNode;
  /** ISO 8601 date-time string representing when the entry was created. */
  time: string;
  /** IANA timezone identifier for the poster (e.g., 'America/Los_Angeles'). */
  timezone?: string | null;
  /** List of comments attached to this entry. */
  comments: EntryCommentType[];
  /** Optional URL for a thumbnail image to display in collapsed view. */
  thumbnail?: string;
  /** Render prop function. Receives expansion state and toggle handler; should render card content. */
  children: (props: { isExpanded: boolean; onToggle: () => void }) => ReactNode;
}

/**
 * Base wrapper component for all journal entry cards.
 * Handles expansion state, provides consistent styling/behavior, and renders common UI elements:
 * - Header with icon, title, time (with timezone), and comment count
 * - Body content (via children render prop)
 * - Comments section (when expanded)
 *
 * Expansion Logic:
 * - Entry is expanded if EITHER the global "expand all" is true OR the entry's individual state is expanded
 * - When user toggles off "expand all", entries revert to their individual expanded states
 * - Individual entry toggles always work, regardless of global state
 */
export const JournalEntryCard: React.FC<JournalEntryCardProps> = ({
  entryId,
  icon,
  title,
  time,
  timezone,
  comments,
  thumbnail,
  children,
}) => {
  const allExpanded = useAtomValue(allEntriesExpandedAtom);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setIsExpanded(allExpanded);
  }, [allExpanded]);

  const onToggle = () => {
    setIsExpanded(!isExpanded);
  };

  /**
   * Format time in poster's timezone
   * Note: If timezone is not provided it will use viewer's local time by default.
   */
  const derivedTimezone = resolveTimezone(timezone);
  const entryDate = parseISO(time);
  const timeString = formatInTimeZone(entryDate, derivedTimezone, 'h:mm a');
  const timezoneAbbr = formatInTimeZone(entryDate, derivedTimezone, 'zzz');
  const timeWithTimezone = `${timeString} ${timezoneAbbr}`;

  const hasComments = comments.length > 0;

  return (
    <Box
      bg="bg.default"
      borderRadius="lg"
      border="1px solid"
      borderColor="border.default"
      p={3}
      cursor="pointer"
      onClick={onToggle}
      transition="all 0.2s ease-in-out"
      _hover={{
        boxShadow: 'md',
        borderColor: 'border.subtle',
      }}
      data-testid={`journal-entry-${entryId}`}
    >
      <VStack align="stretch" spacing={2}>
        {/* Top Row: Time on left, Expand/Collapse icon on right */}
        <HStack justify="space-between" align="center">
          <Text fontSize="xs" color="text.muted">
            {timeWithTimezone}
          </Text>
          <HStack spacing={2}>
            {hasComments && (
              <CommentBadge count={comments.length} fontSize="xs" />
            )}
            <IconButton
              aria-label={isExpanded ? 'Collapse entry' : 'Expand entry'}
              variant="unstyled"
              size="xs"
              icon={
                <Icon
                  as={isExpanded ? MdExpandLess : MdExpandMore}
                  boxSize={4}
                />
              }
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
            />
          </HStack>
        </HStack>

        {/* Content: Icon/title/children on left, thumbnail on right (when collapsed) */}
        <HStack align="flex-start" spacing={3}>
          <VStack align="stretch" spacing={2} flex={1}>
            {/* Icon and title */}
            <HStack spacing={2}>
              {icon}
              {title}
            </HStack>

            {/* Body content from children */}
            {children({ isExpanded, onToggle })}
          </VStack>

          {/* Thumbnail (only when collapsed) */}
          {!isExpanded && thumbnail && (
            <Box
              borderRadius="md"
              overflow="hidden"
              width="25%"
              aspectRatio={1}
              flexShrink={0}
              maxWidth="100px"
            >
              <Image
                src={thumbnail}
                alt="Journal entry thumbnail"
                width="100%"
                height="100%"
                objectFit="cover"
              />
            </Box>
          )}
        </HStack>

        {/* Comments section */}
        {isExpanded && hasComments && (
          <Box
            mt={2}
            pt={2}
            borderTop="1px solid"
            borderColor="border.default"
            onClick={(e) => e.stopPropagation()}
          >
            <Text fontSize="xs" fontWeight={600} color="text.muted" mb={2}>
              Comments ({comments.length})
            </Text>
            <VStack align="stretch" spacing={3}>
              {comments.map((comment) => (
                <EntryComment key={comment.id} comment={comment} />
              ))}
            </VStack>
          </Box>
        )}
      </VStack>
    </Box>
  );
};

JournalEntryCard.displayName = 'JournalEntryCard';
